const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/reducers/index.ts';
import _ from 'lodash';
import { Action, combineReducers, Reducer, ReducersMapObject } from 'redux';

import { RESET_STATE } from '../actions/userActions';
import { casesReducer } from './casesReducer';
import deviceReducer from './deviceReducer';
import { intlReducer } from './intlReducer';
import { kompendiumReducer } from './kompendiumReducer';
import { labReportsReducer } from './labReportsReducer';
import { networkReducer } from './networkReducer';
import { patAppointmentsReducer } from './patAppointmentsReducer';
import { patientsReducer } from './patientsReducer';
import { userReducer } from './userReducer';
import { showUncaughtErrorFromRoot } from './uiReducer';
import { settingsReducer } from './settingsReducer';
import { reducer as workItemsToSignReducer } from '../store/workItemsForDocumentsToSign';
import { ReducerState } from '../defs/actionTypes';
import { emptyMap } from '../utils/generalUtils';

// wrap reducer to handle reset state action on whole state tree
export function resetable(reducer: Reducer) {
    const initialState = reducer(undefined, {} as Action);
    return (state: any = initialState, action: any = {}) => {
        switch (action.type) {
            case RESET_STATE:
                // resetting the routing subtree leads to error under specific timing
                // constraints.
                return Object.assign(initialState, { device: state.device });
            default:
                return reducer(state, action);
        }
    };
}

export const makeReducersWithCatch = (reducers: ReducersMapObject) =>
    _.mapValues(reducers, (reducer: (state: any, action: Action) => any, key: string) => {
        return (state: any, action: Action) => {
            try {
                return reducer(state, action);
            } catch (exc) {
                exc.origin = `${key}Reducer`;
                throw exc;
            }
        };
    });

export function makeResetableReducersWithCatch(reducers: ReducersMapObject) {
    return resetable(
        ((combinedReducers: any) => (state: any, action: Action) => {
            try {
                return combinedReducers(state, action);
            } catch (exc) {
                return showUncaughtErrorFromRoot(state, exc.origin || '???', exc);
            }
        })(combineReducers(makeReducersWithCatch(reducers)))
    );
}

export interface CommonStoreState {
    cases: ReducerState;
    cis4m: ReducerState;
    device: ReducerState;
    events: ReducerState;
    form: ReducerState;
    i18n: ReducerState;
    kompendium: ReducerState;
    labReports: ReducerState;
    login: ReducerState; // FIXME: it looks like THIS SHOULD NOT BE HERE
    network: ReducerState;
    medDiagnoses: ReducerState;
    mediPick: ReducerState; // FIXME: there is a selector in Common that points here, BUT THE REDUCER IS IN KISAPP.....
    metakat: ReducerState;
    orders: ReducerState;
    patAppointments: ReducerState;
    patients: ReducerState;
    pflegeProzess: ReducerState;
    reports: ReducerState;
    settings: ReducerState;
    ui: ReducerState;
    user: ReducerState;
    workItemsToSign: ReducerState;
}

export const commonReducers = {
    patAppointments: patAppointmentsReducer,
    cases: casesReducer,
    device: deviceReducer,
    i18n: intlReducer,
    kompendium: kompendiumReducer,
    labReports: labReportsReducer,
    network: networkReducer,
    patients: patientsReducer,
    settings: settingsReducer,
    user: userReducer,
    workItemsToSign: workItemsToSignReducer,
};

export function createMockCommonStoreState(): CommonStoreState {
    return {
        cases: emptyMap,
        cis4m: emptyMap,
        device: emptyMap,
        events: emptyMap,
        form: emptyMap,
        i18n: emptyMap,
        kompendium: emptyMap,
        labReports: emptyMap,
        login: emptyMap,
        network: emptyMap,
        medDiagnoses: emptyMap,
        mediPick: emptyMap,
        metakat: emptyMap,
        orders: emptyMap,
        patAppointments: emptyMap,
        patients: emptyMap,
        pflegeProzess: emptyMap,
        reports: emptyMap,
        settings: emptyMap,
        ui: emptyMap,
        user: emptyMap,
        workItemsToSign: emptyMap,
    };
}
